import { useCallback, useEffect, useMemo, useState } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { FileText, Plus, Search, Trash2 } from "lucide-react";
import { paths, computeTotals, type Estimate, type LineItem } from "@clevis/shared-types";
import { Button, Card, CardTitle, Input, Label, cn } from "@clevis/shared-ui";
import { db } from "../lib/firebase";
import { call } from "../lib/callFunction";
import { PresentEstimate } from "../pages/sales/PresentEstimate";

// Quote on the job, the way the big field apps do it: the tech (or office)
// builds an estimate from the pricebook (names and prices, never costs) plus
// custom lines, hands the phone over to present and sign, and turns the
// accepted estimate into the invoice the Payment card collects on. Served by
// functions so a Field Tech sees only their own jobs' estimates, cost-free.

interface FieldItem {
  id: string;
  name: string;
  description: string;
  category: string;
  sku: string;
  priceCents: number;
  taxable: boolean;
  kind: "item" | "kit";
  components: Array<{ itemId: string; name: string; quantity: number }>;
  showComponents: boolean;
}
type JobEstimate = Omit<Estimate, "lines" | "customerId" | "jobId" | "sentAt" | "decidedAt" | "expiresAt" | "signaturePngBase64" | "createdByUid" | "updatedAt"> & {
  id: string;
  lines: Array<Omit<LineItem, "unitCostCents">>;
};
interface DraftLine {
  key: string;
  description: string;
  quantity: string;
  price: string;
  taxable: boolean;
  itemId: string | null;
  priceVisibility: "show" | "hide";
  components: LineItem["components"];
  showComponents: boolean;
}

const money = (c: number) => (c / 100).toLocaleString("en-US", { style: "currency", currency: "USD" });
const STATUS_STYLE: Record<string, string> = {
  draft: "bg-steel/15 text-ink",
  sent: "bg-brandorange/15 text-brandorange-dark",
  accepted: "bg-success/15 text-success",
  declined: "bg-steel/10 text-steel line-through",
};
let keySeq = 0;
const blank = (): DraftLine => ({
  key: `l${keySeq++}`,
  description: "",
  quantity: "1",
  price: "",
  taxable: true,
  itemId: null,
  priceVisibility: "show",
  components: null,
  showComponents: true,
});
const toCents = (s: string) => {
  const n = Number.parseFloat(s.replace(/[$,\s]/g, ""));
  return Number.isFinite(n) ? Math.round(Number((n * 100).toFixed(6))) : 0;
};

function toLines(lines: DraftLine[]): LineItem[] {
  return lines
    .filter((l) => l.description.trim() && Number.parseFloat(l.quantity) > 0)
    .map((l) => ({
      description: l.description.trim().slice(0, 500),
      quantity: Number.parseFloat(l.quantity),
      unitPriceCents: toCents(l.price),
      unitCostCents: null, // the server fills pricebook costs in
      taxable: l.taxable,
      itemId: l.itemId,
      priceVisibility: l.priceVisibility,
      components: l.components,
      showComponents: l.showComponents,
    }));
}

/** The job walk's findings and notes as estimate notes. */
export function walkNotes(walk: { findings: string; notes: string[] }): string {
  return [walk.findings, ...walk.notes].map((s) => s.trim()).filter(Boolean).join("\n");
}

function EstimateEditor({
  tenantId,
  initial,
  busy,
  walk,
  onSave,
  onCancel,
}: {
  tenantId: string;
  initial: JobEstimate | null;
  busy: boolean;
  /** The job walk's notes, offered as a starting point. */
  walk?: { findings: string; notes: string[] } | null;
  onSave: (lines: LineItem[], taxRateBps: number, notes: string) => void;
  onCancel: () => void;
}) {
  const [lines, setLines] = useState<DraftLine[]>(
    initial
      ? initial.lines.map((l) => ({
          key: `l${keySeq++}`,
          description: l.description,
          quantity: String(l.quantity),
          price: (l.unitPriceCents / 100).toFixed(2),
          taxable: l.taxable,
          itemId: l.itemId,
          priceVisibility: l.priceVisibility ?? "show",
          components: l.components ?? null,
          showComponents: l.showComponents ?? true,
        }))
      : [blank()],
  );
  const [tax, setTax] = useState(initial ? (initial.taxRateBps / 100).toString() : "0");
  const [notes, setNotes] = useState(initial?.notes ?? "");
  const [items, setItems] = useState<FieldItem[] | null>(null);
  const [search, setSearch] = useState("");
  const [picking, setPicking] = useState(false);

  useEffect(() => {
    call<{ items: FieldItem[] }>("listFieldPricebook", { tenantId }, { mutation: false })
      .then((r) => setItems(r.items))
      .catch(() => setItems([]));
  }, [tenantId]);

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (items ?? []).filter((i) => !q || `${i.name} ${i.description} ${i.category} ${i.sku}`.toLowerCase().includes(q)).slice(0, 40);
  }, [items, search]);

  const set = (key: string, patch: Partial<DraftLine>) => setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  function add(item: FieldItem) {
    const kit = item.kind === "kit" && item.components.length > 0;
    setLines((ls) => [
      ...ls.filter((l) => l.description.trim() !== ""), // an empty starter line gives way
      {
        ...blank(),
        description: item.name + (item.description ? ` — ${item.description}` : ""),
        price: (item.priceCents / 100).toFixed(2),
        taxable: item.taxable,
        itemId: item.id,
        components: kit ? item.components : null,
        showComponents: kit ? item.showComponents : true,
      },
    ]);
    setPicking(false);
    setSearch("");
  }

  const taxBps = Math.min(Math.max(Math.round(Number.parseFloat(tax || "0") * 100), 0), 5000);
  const parsed = toLines(lines);
  const totals = computeTotals(parsed, taxBps);

  return (
    <div className="mt-2 space-y-2 rounded-lg bg-cream p-2.5">
      {picking ? (
        <div className="rounded-lg bg-white p-2">
          <div className="flex items-center gap-2">
            <Search size={14} className="text-steel" />
            <Input autoFocus className="py-1.5" placeholder="Search the pricebook" value={search} onChange={(e) => setSearch(e.target.value)} />
            <button className="text-xs text-steel underline" onClick={() => setPicking(false)}>
              Close
            </button>
          </div>
          <div className="mt-1 max-h-64 divide-y divide-steel/10 overflow-y-auto">
            {items === null ? <p className="py-2 text-xs text-steel">Loading…</p> : null}
            {items && shown.length === 0 ? <p className="py-2 text-xs text-steel">Nothing matches.</p> : null}
            {shown.map((i) => (
              <button key={i.id} className="flex w-full items-center justify-between gap-2 py-1.5 text-left hover:bg-cream/60" onClick={() => add(i)}>
                <span className="min-w-0 flex-1 truncate text-sm text-ink">
                  {i.name}
                  {i.kind === "kit" ? <span className="ml-1 text-[10px] text-steel">kit</span> : null}
                </span>
                <span className="shrink-0 text-sm text-ink">{money(i.priceCents)}</span>
              </button>
            ))}
          </div>
        </div>
      ) : null}

      {lines.map((l, i) => (
        <div key={l.key} className="flex items-end gap-1.5">
          <div className="min-w-0 flex-1">
            {i === 0 ? <Label>Work / item</Label> : null}
            <Input value={l.description} onChange={(e) => set(l.key, { description: e.target.value })} placeholder="Describe the work" />
          </div>
          <div className="w-14">
            {i === 0 ? <Label>Qty</Label> : null}
            <Input inputMode="decimal" value={l.quantity} onChange={(e) => set(l.key, { quantity: e.target.value })} />
          </div>
          <div className="w-24">
            {i === 0 ? <Label>Price</Label> : null}
            <Input inputMode="decimal" value={l.price} onChange={(e) => set(l.key, { price: e.target.value })} />
          </div>
          <button type="button" aria-label="Remove line" className="pb-2.5 text-steel hover:text-danger" onClick={() => setLines((ls) => (ls.length > 1 ? ls.filter((x) => x.key !== l.key) : [blank()]))}>
            <Trash2 size={14} />
          </button>
        </div>
      ))}

      <div className="flex flex-wrap items-center gap-2">
        <Button type="button" variant="ghost" className="px-2 py-1 text-xs" onClick={() => setPicking(true)}>
          <Search size={13} /> Pricebook
        </Button>
        <Button type="button" variant="ghost" className="px-2 py-1 text-xs" onClick={() => setLines((ls) => [...ls, blank()])}>
          <Plus size={13} /> Custom line
        </Button>
        <div className="ml-auto flex items-center gap-1">
          <Label className="mb-0">Tax %</Label>
          <Input className="w-16 py-1" inputMode="decimal" value={tax} onChange={(e) => setTax(e.target.value)} />
        </div>
      </div>
      <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes for the customer (optional)" />
      {walk && walkNotes(walk) && notes !== walkNotes(walk) ? (
        <button type="button" className="mt-1 text-xs text-brandorange-dark underline" onClick={() => setNotes(walkNotes(walk))}>
          Use the job walk's notes
        </button>
      ) : null}
      <div className="flex items-center justify-between">
        <span className="text-sm text-ink">
          Total <strong>{money(totals.totalCents)}</strong>
        </span>
        <div className="flex items-center gap-2">
          <button className="text-xs text-steel underline hover:text-ink" onClick={onCancel}>
            Cancel
          </button>
          <Button className="px-3 py-1 text-xs" busy={busy} disabled={parsed.length === 0} onClick={() => onSave(parsed, taxBps, notes)}>
            {initial ? "Save estimate" : "Create estimate"}
          </Button>
        </div>
      </div>
    </div>
  );
}

export function JobEstimates({
  tenantId,
  jobId,
  payerId,
  walk,
  onInvoiced,
}: {
  tenantId: string;
  jobId: string;
  /** What the job walk found (parent job's jobWalk), offered as estimate notes. */
  walk?: { findings: string; notes: string[] } | null;
  /** The job's payer (bill-to, else customer): office estimates go to them too. */
  payerId: string | null;
  onInvoiced: () => void;
}) {
  const [estimates, setEstimates] = useState<JobEstimate[] | null>(null);
  const [editing, setEditing] = useState<"new" | JobEstimate | null>(null);
  const [presenting, setPresenting] = useState<JobEstimate | null>(null);
  const [companyName, setCompanyName] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const res = await call<{ estimates: JobEstimate[] }>("getJobEstimates", { tenantId, jobId }, { mutation: false });
      setEstimates(res.estimates);
    } catch (err) {
      setError((err as Error).message);
      setEstimates([]);
    }
  }, [tenantId, jobId]);
  useEffect(() => void load(), [load]);
  useEffect(() => onSnapshot(doc(db, paths.tenant(tenantId)), (s) => setCompanyName((s.get("name") as string) ?? "")), [tenantId]);

  async function act(key: string, fn: () => Promise<unknown>, after?: () => void) {
    setBusy(key);
    setError("");
    try {
      await fn();
      after?.();
      await load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(null);
    }
  }

  async function pdf(e: JobEstimate) {
    await act(`pdf${e.id}`, async () => {
      const res = await call<{ base64: string; filename: string }>("getSalesDocPdf", { tenantId, docId: e.id, kind: "estimate" }, { mutation: false });
      const a = document.createElement("a");
      a.href = `data:application/pdf;base64,${res.base64}`;
      a.download = res.filename;
      a.click();
    });
  }

  if (presenting) {
    // PresentEstimate is the customer-facing screen: prices only, never costs.
    const asEstimate = { ...presenting, lines: presenting.lines.map((l) => ({ ...l, unitCostCents: null })) } as unknown as Estimate & { id: string };
    return (
      <PresentEstimate
        estimate={asEstimate}
        companyName={companyName}
        onExit={() => {
          setPresenting(null);
          void load();
          onInvoiced();
        }}
      />
    );
  }

  return (
    <Card>
      <div className="flex items-center justify-between">
        <CardTitle className="mb-0">Estimates</CardTitle>
        {!editing ? (
          <Button variant="ghost" className="px-2 py-1 text-xs" onClick={() => setEditing("new")}>
            <Plus size={14} /> New estimate
          </Button>
        ) : null}
      </div>
      {error ? <p className="mt-1 text-xs text-danger">{error}</p> : null}

      {editing ? (
        <EstimateEditor
          tenantId={tenantId}
          initial={editing === "new" ? null : editing}
          walk={walk}
          busy={busy === "save"}
          onCancel={() => setEditing(null)}
          onSave={(lines, taxRateBps, notes) =>
            void act(
              "save",
              () =>
                editing === "new"
                  ? call("createEstimate", { tenantId, jobId, customerId: payerId, lines, taxRateBps, notes })
                  : call("updateSalesDoc", { tenantId, docId: editing.id, kind: "estimate", lines, taxRateBps, notes }),
              () => setEditing(null),
            )
          }
        />
      ) : null}

      {estimates === null ? (
        <p className="mt-1 text-xs text-steel">Loading…</p>
      ) : estimates.length === 0 && !editing ? (
        <p className="mt-1 text-xs text-steel">Quote the work here: pricebook items or your own lines, then present it for the customer to sign.</p>
      ) : (
        <div className="mt-2 divide-y divide-steel/10">
          {estimates.map((e) => (
            <div key={e.id} className="py-2">
              <div className="flex items-center justify-between gap-2">
                <span className="min-w-0 flex-1 truncate text-sm font-medium text-ink">
                  #{e.number} · {money(e.totalCents)}
                </span>
                <span className={cn("rounded-full px-2 py-0.5 text-xs", STATUS_STYLE[e.status])}>{e.status}</span>
              </div>
              <p className="truncate text-xs text-steel">
                {e.lines.map((l) => l.description).join(" · ")}
                {e.signerName ? ` · signed by ${e.signerName}` : ""}
              </p>
              <div className="mt-1 flex flex-wrap items-center gap-1.5">
                {e.status === "draft" || e.status === "sent" ? (
                  <Button className="px-2.5 py-1 text-xs" onClick={() => setPresenting(e)}>
                    Present &amp; sign
                  </Button>
                ) : null}
                {e.status === "draft" ? (
                  <Button variant="ghost" className="px-2 py-1 text-xs" onClick={() => setEditing(e)}>
                    Edit
                  </Button>
                ) : null}
                {e.status === "accepted" && !e.invoiceId ? (
                  <Button
                    variant="secondary"
                    className="px-2.5 py-1 text-xs"
                    busy={busy === `inv${e.id}`}
                    onClick={() =>
                      void act(`inv${e.id}`, () => call("convertEstimateToInvoice", { tenantId, estimateId: e.id }), onInvoiced)
                    }
                  >
                    Make the invoice
                  </Button>
                ) : null}
                {e.invoiceId ? <span className="text-xs text-success">Invoiced, see Payment</span> : null}
                {e.status === "sent" ? (
                  <Button
                    variant="ghost"
                    className="px-2 py-1 text-xs"
                    busy={busy === `dec${e.id}`}
                    onClick={() => void act(`dec${e.id}`, () => call("setEstimateStatus", { tenantId, estimateId: e.id, action: "decline" }))}
                  >
                    Declined
                  </Button>
                ) : null}
                <Button variant="ghost" className="px-2 py-1 text-xs" busy={busy === `pdf${e.id}`} onClick={() => void pdf(e)}>
                  <FileText size={13} /> PDF
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
